"use client"

import { useReducedMotion } from "framer-motion"
import { cn } from "@/shared/lib/utils"

const ASPECT_RATIOS = ["aspect-square", "aspect-[3/4]", "aspect-[4/3]"] as const

// Deterministic "random" aspect ratio based on index
function getAspectRatio(index: number): string {
  // Pattern that looks natural: portrait-heavy mix
  const pattern = [1, 0, 1, 2, 1, 1, 0, 1, 2, 1, 0, 1]
  return ASPECT_RATIOS[pattern[index % pattern.length]]
}

/** Single skeleton card — use inside masonry columns for inline loading placeholders. */
export function SkeletonCard({ index, prefersReducedMotion }: { index: number; prefersReducedMotion: boolean | null }) {
  return (
    <div
      className={cn(
        "relative overflow-hidden bg-card",
        getAspectRatio(index)
      )}
    >
      <div
        className={cn(
          "absolute inset-0 bg-gradient-to-br from-muted/80 via-muted/40 to-muted/80",
          prefersReducedMotion ? "opacity-80" : "animate-pulse"
        )}
      />
      <div
        className={cn(
          "absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent",
          prefersReducedMotion
            ? "opacity-40"
            : "-translate-x-full animate-[shimmer_1.5s_infinite]"
        )}
      />
      <div className="absolute inset-x-0 bottom-0 p-3 space-y-2">
        <div className="flex items-center gap-2">
          <div className="size-5 rounded-full bg-white/10" />
          <div className="h-3 w-16 rounded-full bg-white/10" />
        </div>
        <div className="h-2.5 w-3/4 rounded-full bg-white/8" />
      </div>
    </div>
  )
}

interface GallerySkeletonProps {
  count?: number
}

export function GallerySkeleton({ count = 12 }: GallerySkeletonProps) {
  const prefersReducedMotion = useReducedMotion()
  const items = Array.from({ length: count }, (_, i) => i)

  // Distribute into 5 columns like the real masonry grid.
  const columns: number[][] = Array.from({ length: 5 }, () => [])
  const heights = Array.from({ length: 5 }, () => 0)
  const heightMap: Record<string, number> = {
    "aspect-square": 1,
    "aspect-[3/4]": 1.33,
    "aspect-[4/3]": 0.75,
  }

  items.forEach((idx) => {
    const aspect = getAspectRatio(idx)
    const shortest = heights.indexOf(Math.min(...heights))
    columns[shortest].push(idx)
    heights[shortest] += heightMap[aspect] || 1
  })

  return (
    <div className="flex gap-px">
      {columns.map((column, colIndex) => (
        <div
          key={colIndex}
          className={cn(
            "flex flex-1 flex-col gap-px",
            colIndex >= 2 && "hidden sm:flex",
            colIndex >= 3 && "hidden md:flex",
            colIndex >= 4 && "hidden xl:flex"
          )}
        >
          {column.map((idx) => (
            <div
              key={idx}
              className={cn(
                "relative overflow-hidden bg-card",
                getAspectRatio(idx)
              )}
            >
              {/* Pulsing gradient base */}
              <div
                className={cn(
                  "absolute inset-0 bg-gradient-to-br from-muted/80 via-muted/40 to-muted/80",
                  prefersReducedMotion ? "opacity-80" : "animate-pulse"
                )}
              />
              {/* Shimmer sweep — visible */}
              <div
                className={cn(
                  "absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent",
                  prefersReducedMotion
                    ? "opacity-40"
                    : "-translate-x-full animate-[shimmer_1.5s_infinite]"
                )}
              />
              {/* Bottom bar simulating the overlay info */}
              <div className="absolute inset-x-0 bottom-0 p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <div className="size-5 rounded-full bg-white/10" />
                  <div className="h-3 w-16 rounded-full bg-white/10" />
                </div>
                <div className="h-2.5 w-3/4 rounded-full bg-white/8" />
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}
